(function () {
  if (window.__cartShippingMeterLoaded) {
    return;
  }

  window.__cartShippingMeterLoaded = true;

  function rootPath() {
    var root =
      window.Shopify && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : "/";

    if (root.slice(-1) !== "/") {
      root += "/";
    }

    return root;
  }

  class CartShippingMeter extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.debounceTimer = null;
      this.lastTotal = null;
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onMarkupUpdated = this.onMarkupUpdated.bind(this);
    }

    connectedCallback() {
      // Thresholds are emitted by Liquid in store-currency cents; the
      // presentment rate is applied in currencyRate() for Markets visitors.
      // getAttribute, not dataset: "data-threshold-1" keeps its hyphen-digit
      // suffix in the dataset key, so dataset.threshold1 is undefined.
      this.threshold1 = parseInt(this.getAttribute("data-threshold-1"), 10) || 0;
      this.threshold2 = parseInt(this.getAttribute("data-threshold-2"), 10) || 0;

      this.messageEl = this.querySelector("[data-meter-message]");
      this.barEl = this.querySelector("[data-meter-bar]");
      this.fillEl = this.querySelector("[data-meter-fill]");
      this.stops = Array.from(this.querySelectorAll("[data-meter-stop]"));

      var configNode = this.querySelector("[data-meter-config]");

      if (configNode) {
        try {
          this.config = JSON.parse(configNode.textContent);
        } catch (error) {
          this.config = {};
        }
      }

      if (!this.threshold1 || !this.messageEl || !this.fillEl) {
        return;
      }

      document.addEventListener("cart:updated", this.onCartUpdated);
      document.addEventListener("ajaxProduct:added", this.onProductAdded);
      document.addEventListener("cart:markup-updated", this.onMarkupUpdated);

      this.render(parseInt(this.dataset.cartTotal, 10) || 0);

      // The server-rendered total can be stale when the page came from a
      // cache, so re-check once on load.
      this.scheduleRefresh();
    }

    disconnectedCallback() {
      document.removeEventListener("cart:updated", this.onCartUpdated);
      document.removeEventListener("ajaxProduct:added", this.onProductAdded);
      document.removeEventListener("cart:markup-updated", this.onMarkupUpdated);

      if (this.debounceTimer) {
        window.clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      if (this.verifyTimer) {
        window.clearTimeout(this.verifyTimer);
        this.verifyTimer = null;
      }
    }

    onCartUpdated(event) {
      var cart = event && event.detail ? event.detail.cart : null;

      if (cart && typeof cart.total_price === "number") {
        this.render(cart.total_price);
        return;
      }

      this.scheduleRefresh();
    }

    onProductAdded(event) {
      // Optimistic instant update: the cart/add.js response rides on the
      // event, so the bar can move before any network round-trip. The exact
      // total follows via cart:markup-updated when the drawer rebuilds.
      var item = event && event.detail ? event.detail.product : null;
      var delta = 0;

      if (item) {
        if (typeof item.final_line_price === "number") {
          delta = item.final_line_price;
        } else if (typeof item.final_price === "number") {
          delta = item.final_price * (item.quantity || 1);
        } else if (typeof item.price === "number") {
          delta = item.price * (item.quantity || 1);
        }
      }

      if (delta > 0 && typeof this.lastTotal === "number") {
        this.render(this.lastTotal + delta);
      } else {
        this.scheduleRefresh();
      }

      // Second look after any cart-read caching layers (e.g. the cart read
      // broker's post-write window) have expired, so a stale or optimistic
      // total can never stick.
      if (this.verifyTimer) {
        window.clearTimeout(this.verifyTimer);
      }

      this.verifyTimer = window.setTimeout(() => {
        this.verifyTimer = null;
        this.refresh();
      }, 4200);
    }

    onMarkupUpdated(event) {
      var subtotal = event && event.detail ? event.detail.subtotal : null;

      if (typeof subtotal === "number" && !isNaN(subtotal)) {
        // Exact section-rendered total; supersedes any pending fetch.
        if (this.debounceTimer) {
          window.clearTimeout(this.debounceTimer);
          this.debounceTimer = null;
        }

        this.render(subtotal);
      }
    }

    scheduleRefresh() {
      if (this.debounceTimer) {
        window.clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = window.setTimeout(() => {
        this.debounceTimer = null;
        this.refresh();
      }, 150);
    }

    refresh() {
      fetch(rootPath() + "cart.js?t=" + Date.now(), {
        credentials: "same-origin",
        cache: "no-store",
      })
        .then((response) => response.json())
        .then((cart) => {
          if (cart && typeof cart.total_price === "number") {
            this.render(cart.total_price);
          }
        })
        .catch(() => {});
    }

    currencyRate() {
      var rate =
        window.Shopify && Shopify.currency && Shopify.currency.rate
          ? parseFloat(Shopify.currency.rate)
          : 1;

      return rate > 0 ? rate : 1;
    }

    formatMoney(cents) {
      if (
        window.theme &&
        theme.Currency &&
        typeof theme.Currency.formatMoney === "function" &&
        theme.settings
      ) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }

      return "$" + (cents / 100).toFixed(2);
    }

    render(total) {
      this.lastTotal = total;
      var rate = this.currencyRate();
      var threshold1 = Math.round(this.threshold1 * rate);
      var threshold2 = this.threshold2
        ? Math.round(this.threshold2 * rate)
        : 0;
      var hasTier2 = threshold2 > threshold1;
      var goal = hasTier2 ? threshold2 : threshold1;

      var pct = Math.min(100, Math.round((total / goal) * 100));
      this.fillEl.style.width = pct + "%";

      if (this.barEl) {
        this.barEl.setAttribute("aria-valuenow", pct);
      }

      this.stops.forEach((stop) => {
        var stopThreshold =
          stop.dataset.meterStop === "2" ? threshold2 : threshold1;
        stop.classList.toggle("is-unlocked", total >= stopThreshold);
      });

      var message;

      if (total < threshold1) {
        message = (this.config.messageBelow1 || "").replace(
          "[amount]",
          this.formatMoney(threshold1 - total)
        );
      } else if (hasTier2 && total < threshold2) {
        message = (this.config.messageBelow2 || "").replace(
          "[amount]",
          this.formatMoney(threshold2 - total)
        );
      } else {
        message = hasTier2 ? this.config.unlocked2 : this.config.unlocked1;
      }

      this.messageEl.textContent = message || "";
      this.classList.toggle("is-complete", total >= goal);
    }
  }

  if (!customElements.get("cart-shipping-meter")) {
    customElements.define("cart-shipping-meter", CartShippingMeter);
  }
})();
